import React from "react"
import {
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryAxis,
  VictoryLabel,
} from "victory"
import { NO_DATA, NOT_ENOUGH_DATA } from "@swipesense/compliance-helpers"
import PropTypes from "prop-types"
import LineGraphMessage from "./LineGraphMessage"
import moment from "moment"

const allValuesMatch = (groups, match) => {
  const allCompliance = groups
    .map(group => group.data.map(d => d.compliance))
    .reduce((acc, compliance) => [...acc, ...compliance], [])
  return allCompliance.filter(d => d === match).length === allCompliance.length
}

const lineLabel = compliance => {
  switch (compliance) {
    case NO_DATA:
      return { text: " ⃠ data", color: "#C5C1C0", fontSize: "10px" }
    case NOT_ENOUGH_DATA:
      return { text: " ⃠ data", color: "#C5C1C0", fontSize: "10px" }
    default:
      return { text: `${Math.round(compliance)}%`, color: "" }
  }
}

const timeRangeToDateLabel = timeRange => {
  if (!timeRange) {
    return null
  }

  const start = moment(timeRange[0])
  const end = moment(timeRange[1])
  const daysShown = end.diff(start, "days") + 1
  if (daysShown === 1) {
    // start and end dates are the same
    return start.format("ddd MMM D")
  } else if (start.month() === end.month()) {
    // start and end dates are in the same month
    const lastDayOfMonth = moment(end)
      .endOf("month")
      .date()
    return start.date() === 1 && end.date() === lastDayOfMonth
      ? start.format("MMM") // whole month
      : `${start.format("MMM D")}-${end.format("D")}` // part of month
  } else {
    // start and end dates are in different months
    return `${start.format("MMM D")} - ${end.format("MMM D")}`
  }
}

// These may need tweaking during the polishing phase.
const WIDTH = 750 // Arbitrary number. All other widths are relative to this.
const HEIGHT = 250 // Arbitrary number. All other heights are relative to this.
const SIDE_PADDING = 120
const SPACE_BETWEEN_LINES = 4
const FONT_FAMILY = '"Inter", ",sans-serif", "Arial"' // should match $font-family-base in bootstrap-variables.scss

class LineGraph extends React.Component {
  constructor(props) {
    super(props)

    this.state = { hoveredIndex: null }
  }

  render() {
    const { groups, timeRanges } = this.props

    // Goal: condense y-axis to show small changes in data better
    // Find largest and smallest scores and start by setting max/min of y-axis to those
    let maxYaxis = 0
    let minYaxis = 100

    // Note BSR toggle. If not toggled, there is one group. If toggled, there is three (overall, 1st, 2nd)
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i]

      for (let i = 0; i < group.data.length; i++) {
        const compliance = group.data[i].compliance

        if (compliance > maxYaxis) maxYaxis = compliance
        if (compliance < minYaxis) minYaxis = compliance
      }
    }

    // Now create some buffer (15 seems about right) above max and below min scores, unless a score is close to 0 or 100 (no need to taper)
    // Negative compliance numbers are possible, for some reason, so a negative minYaxis is possible at this point in the logic (negative maxYaxis not possible), but handled by ternary.
    minYaxis > 15 ? (minYaxis -= 15) : (minYaxis = 0)
    maxYaxis < 85 ? (maxYaxis += 15) : (maxYaxis = 100)

    const dataPointWidth = (WIDTH - SIDE_PADDING) / groups[0].data.length
    const spaceBetweenDataPoints = dataPointWidth / 3
    const lineWidth =
      (dataPointWidth -
        spaceBetweenDataPoints -
        SPACE_BETWEEN_LINES * groups.length) /
      groups.length
    const groupOffset = lineWidth + SPACE_BETWEEN_LINES
    const domainPadding = dataPointWidth / 2

    const noData = allValuesMatch(groups, NO_DATA)
    const notEnoughData = allValuesMatch(groups, NOT_ENOUGH_DATA)
    const showData = !(noData || notEnoughData)

    const groupsWithPoints = groups.map(category => {
      return {
        ...category,
        data: category.data.map((d, i) => ({
          ...d,
          // The x values should start at 1 so they don't run into the axis.
          x: i + 1,
          // Set the y value to 0 to ensure an empty bar displays (with a label) when
          // the y value is NO_DATA or NOT_ENOUGH_DATA. The compliance key will contain the actual
          // value, which will be used to display the 0% or ∅ label.
          y: d.compliance >= 0 ? d.compliance : 0,
        })),
      }
    })

    return (
      <div className="LineGraph">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
          <defs>
            {groups.map(({ gradient }, i) => (
              <linearGradient
                key={i}
                id={`gradient${i}`}
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={gradient[0]} />
                <stop offset="100%" stopColor={gradient[1]} />
              </linearGradient>
            ))}
          </defs>
          <VictoryChart
            standalone={false}
            width={WIDTH}
            height={HEIGHT}
            domain={{ y: [minYaxis, maxYaxis] }}
            domainPadding={{ x: domainPadding }}
          >
            <VictoryAxis // x-axis
              className="xAxis"
              tickFormat={x =>
                showData ? timeRangeToDateLabel(timeRanges[x - 1]) : ""
              }
              tickCount={timeRanges.length || undefined}
              style={{ tickLabels: { fontSize: 12, fontFamily: FONT_FAMILY } }}
            />
            <VictoryAxis // y-axis
              dependentAxis
              crossAxis
              tickValues={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
              tickFormat={y => ([0, 100].includes(y) ? "" : `${y}%`)}
              style={{
                axis: { visibility: "hidden" },
                grid: { stroke: "#E0E0E0" }, // horizontal lines at each tick on the y-axis
                tickLabels: { fontSize: 12, fontFamily: FONT_FAMILY },
              }}
            />

            {showData && (
              <VictoryGroup
                key="coloredBars"
                offset={groupOffset}
                style={{ data: { width: lineWidth } }}
              >
                {groupsWithPoints.map(({ data }, i) => (
                  <VictoryLine
                    key={i}
                    data={data}
                    labelComponent={<VictoryLabel dy={10} />}
                    style={{
                      data: {
                        stroke: () => `url(#gradient${i})`,
                      },
                      labels: {
                        stroke: d => lineLabel(d.compliance).color,
                        fontSize: 12,
                        fontWeight: "bolder",
                        fill: `url(#gradient${i})`,
                      },
                    }}
                    labels={d => lineLabel(d.compliance).text}
                    interpolation="natural"
                  />
                ))}
              </VictoryGroup>
            )}

            {noData && (
              <LineGraphMessage
                message="No data found. Try a new search or date range."
                width={WIDTH}
                height={HEIGHT}
              />
            )}

            {notEnoughData && (
              <LineGraphMessage
                message="Not enough data. Try increasing your date range."
                width={WIDTH}
                height={HEIGHT}
              />
            )}
          </VictoryChart>
        </svg>
      </div>
    )
  }
}

LineGraph.propTypes = {
  timeRanges: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  ).isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      hoverLabel: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          compliance: PropTypes.number,
          change: PropTypes.number,
          opportunities: PropTypes.number.isRequired,
        }).isRequired
      ).isRequired,
      gradient: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    }).isRequired
  ).isRequired,
}

LineGraph.defaultProps = {
  timeRanges: [],
}

export default LineGraph
